// Direct-load OHLCV prefetch. Candles have no coverage dependency, so the
// chart's initial seed does not need to wait for the PairPage chunk, the
// mount, or the container-width measurement: on a direct pair URL the exact
// request shape is known the moment the module-init /resolve answers with
// the pool address. Firing it there removes mount + ACK + two React frames
// from the candle critical path.
//
// The chart seeds one viewport of 1m USD candles with no explicit from/to
// (chartSeedRequestForViewport: priorViewport is null on a direct load).
// The bar limit is viewport-derived and clamped to [96, 416], so the
// prefetch asks for the maximum clamp and consumePairOhlcvPrefetch slices
// the response down to the exact requested limit — newest-N, byte-identical
// to what a fresh limit=N read returns against the same snapshot. tail_from
// / tail_trades bound the NEW end of the series and are unaffected by
// dropping older prefix candles.
//
// Freshness contract (owner rule: no caching of market data): consume-once,
// PREFETCH_TTL_MS matches the established pair-seed hover prefetch. Any
// param shape outside the direct-load initial seed (a GRAM toggle, a
// resolution switch, an explicit range, a mode-qualified chart) misses the
// key and fetches normally.

import { type FetchOHLCVParams, fetchOHLCV } from '../api/ohlcv'
import type { OHLCVResponse } from '../api/types'

const PREFETCH_LIMIT = 416
const PREFETCH_TTL_MS = 2_500
const MAX_PREFETCHES = 2

interface Entry {
  startedAt: number
  promise: Promise<OHLCVResponse>
}

const entries = new Map<string, Entry>()

export function startPairOhlcvPrefetch(poolAddr: string): void {
  if (!poolAddr) return
  const now = Date.now()
  const existing = entries.get(poolAddr)
  if (existing && now - existing.startedAt < PREFETCH_TTL_MS) return
  entries.delete(poolAddr)
  while (entries.size >= MAX_PREFETCHES) {
    const oldest = entries.keys().next().value
    if (oldest == null) break
    entries.delete(oldest)
  }
  const promise = fetchOHLCV({
    poolAddr,
    resolution: '1m',
    denom: 'usd',
    limit: PREFETCH_LIMIT,
  })
  entries.set(poolAddr, { startedAt: now, promise })
  // An unconsumed rejection must never surface as an unhandled rejection.
  void promise.catch(() => undefined)
}

/** Consume-once handoff for the chart's initial seed request. Returns the
    prefetched response sliced to the exact requested limit, only when every
    other param matches the direct-load initial seed shape and the entry is
    younger than the TTL; the entry is dropped either way. */
export function consumePairOhlcvPrefetch(
  params: FetchOHLCVParams
): Promise<OHLCVResponse> | undefined {
  if (params.resolution !== '1m' || params.denom !== 'usd') return undefined
  if (params.from != null || params.to != null) return undefined
  if (params.phase || params.source || params.dex) return undefined
  if (params.limit == null || params.limit > PREFETCH_LIMIT) return undefined
  const entry = entries.get(params.poolAddr)
  if (!entry) return undefined
  entries.delete(params.poolAddr)
  if (Date.now() - entry.startedAt >= PREFETCH_TTL_MS) return undefined
  const limit = params.limit
  const derived = entry.promise.then((response) => {
    if (response.candles.length <= limit) return response
    // candles is a historical prefix; keep the N buckets closest to the tail
    // (the consumer re-sorts ascending, so emit order is free). The server
    // cursor belongs to the unsliced 416-bar response, so rebase it to the
    // earliest candle we actually hand to the chart. Preserving the original
    // cursor would skip every sliced-away candle on the first left-page read.
    const candles = [...response.candles].sort((a, b) => b.t - a.t).slice(0, limit)
    const earliestKept = Math.min(...candles.map((c) => c.t))
    return {
      ...response,
      candles,
      next_before: earliestKept - 1,
      has_more_older: true,
    }
  })
  // The consumer (useOHLCV) attaches its own catch; guard the derived chain
  // anyway so a consumed-then-abandoned request can never leak an unhandled
  // rejection.
  void derived.catch(() => undefined)
  return derived
}

export function clearPairOhlcvPrefetchForTest(): void {
  entries.clear()
}
